// The site-first intake, as a pure model.
//
// Ben's ruling, 2026-08-25: the building is asked FIRST. A BDD comes off a walkthrough
// holding a building. If the first box says "Customer" they type the building, because
// that is what they have and the box is empty and required. If the first box says
// "Facility name" they type the building, and it is correct. By the time step 2 asks who
// signs, the building is on screen above the field, so the second question can only read
// as a different question.
//
// This file holds the step order, what each step requires, and the help copy — all pure,
// so the words can be asserted in a node test (TCSD-0097: only the surface's copy is
// checkable, and the copy is usually the part that is wrong).

export const INTAKE_STEPS = ['building', 'customer', 'service', 'subcontractor', 'review'] as const;
export type IntakeStep = (typeof INTAKE_STEPS)[number];

export const STEP_LABELS: Record<IntakeStep, string> = {
  building: 'The building',
  customer: 'Who signs & who pays',
  service: 'Service & pricing',
  subcontractor: 'Subcontractor',
  review: 'Review',
};

/** The question at the top of each step. Step 2 interpolates the building on purpose. */
export function stepHeading(step: IntakeStep, facilityName: string): string {
  switch (step) {
    case 'building':
      return 'What building are we cleaning?';
    case 'customer':
      // The contrast device, and it is not optional: the building is visibly
      // answered, so "who signs" can only read as a different question.
      return `Who signs the agreement for ${facilityName.trim() || 'this building'}?`;
    case 'service':
      return 'Service and pricing';
    case 'subcontractor':
      return 'Who is cleaning it?';
    case 'review':
      return 'Confirm and create';
  }
}

export function stepSubheading(step: IntakeStep): string {
  switch (step) {
    case 'building':
      return "Start with the site. You'll tell us who signs the contract on the next screen.";
    case 'customer':
      return 'The organization, not the building. If they already have one site with us, they’re in the list — pick them.';
    case 'service':
      return 'What we are charging, and on what terms.';
    case 'subcontractor':
      return 'Leave blank if the sub is not settled yet — the card will show as missing it.';
    case 'review':
      return 'One card, one account link, one facility record.';
  }
}

/** Field help, verbatim from the approved mockup. Do not reword without Ben. */
export const FACILITY_NAME_HELP = {
  ask: 'The name your AM and your cleaner would use for this building.',
  body:
    'This is the name that shows up everywhere. On the pipeline card, in My Work, on the AM’s ' +
    'route, in inspections, and in Finance’s monthly reporting. Write it the way someone standing ' +
    'in the parking lot would say it — include the city or suburb if the customer has more than ' +
    'one location.',
  good: ['DaVita — Live Oak (San Antonio)', 'The Star MOB', 'Ascentist — Blue Ridge PT'],
  bad: [
    { text: 'DaVita', why: 'which one?' },
    { text: 'FSSP', why: 'initials aren’t a name' },
    { text: 'Day Porter', why: 'that’s a service, not a building' },
  ],
} as const;

export const CUSTOMER_HELP = {
  ask: 'The legal entity on the contract. Often the parent company, not this location.',
  body:
    'Customer is who we bill and who signed — not the building. For a chain, that’s the ' +
    'corporate entity: DaVita, Inc., not DaVita Live Oak. For a one-location surgery center, ' +
    'the customer and the building genuinely do share a name — pick the existing customer if ' +
    'one is listed, and only create a new one if nothing matches.',
  good: ['DaVita, Inc. for every DaVita clinic', 'Fresenius Medical Care', 'Ascentist Healthcare'],
  bad: [
    {
      text: 'DaVita Live Oak - San Antonio',
      why: 'that’s the building, and it already lives in Step 1',
    },
  ],
} as const;

export const BILL_TO_HELP = {
  ask: 'Leave blank unless invoices go somewhere other than the customer.',
  body:
    'Use this when a landlord, property manager, or corporate AP pays instead of the entity ' +
    'that signed.',
  example:
    'The Star MOB is a ProStar Commercial Real Estate building — ProStar pays, the tenant ' +
    'signed. That’s a bill-to, not a second account.',
} as const;

export type IntakeAnswers = {
  facility_name: string;
  client_address_street: string;
  client_address_city: string;
  client_address_state: string;
  client_address_zip: string;
  facility_type: string;
  market_region: string;
  /** Set only by choosing from the picker, or by explicitly creating a customer. */
  account_id: string;
  account_name: string;
};

/**
 * What is still missing on a step, in words a BDD can act on. Empty array = can advance.
 *
 * The address is required from step 1 because the trigger that files a card cannot tell
 * which building it is without one, and used to guess.
 */
export function blockingReasons(step: IntakeStep, a: IntakeAnswers): string[] {
  const out: string[] = [];
  const has = (v: string) => v.trim().length > 0;

  if (step === 'building') {
    if (!has(a.facility_name)) out.push('Name the building.');
    if (!has(a.client_address_street)) out.push('Add the street address.');
    if (!has(a.client_address_city)) out.push('Add the city.');
    if (!has(a.client_address_state)) out.push('Add the state.');
    if (!has(a.client_address_zip)) out.push('Add the ZIP.');
    if (!has(a.facility_type)) out.push('Choose a facility type.');
    if (!has(a.market_region)) out.push('Choose a market.');
  }

  if (step === 'customer') {
    // account_name alone is not enough. A chosen customer means an id, which is what
    // stops the database re-deriving the customer from the typed text.
    if (!has(a.account_id) && !has(a.account_name)) {
      out.push('Choose the customer who signs, or create a new one.');
    }
  }

  return out;
}

export function canAdvance(step: IntakeStep, a: IntakeAnswers): boolean {
  return blockingReasons(step, a).length === 0;
}

export function nextStep(step: IntakeStep): IntakeStep {
  const i = INTAKE_STEPS.indexOf(step);
  return INTAKE_STEPS[Math.min(i + 1, INTAKE_STEPS.length - 1)];
}

export function prevStep(step: IntakeStep): IntakeStep {
  const i = INTAKE_STEPS.indexOf(step);
  return INTAKE_STEPS[Math.max(i - 1, 0)];
}

export type StepRailItem = { step: IntakeStep; label: string; state: 'done' | 'on' | 'todo' };

export function stepRail(current: IntakeStep): StepRailItem[] {
  const at = INTAKE_STEPS.indexOf(current);
  return INTAKE_STEPS.map((step, i) => ({
    step,
    label: STEP_LABELS[step],
    state: i < at ? 'done' : i === at ? 'on' : 'todo',
  }));
}

/**
 * What the footer of the review step states plainly: whether this creates a customer or
 * attaches to one that already exists. The BDD should never have to infer it.
 */
export function reviewFooterSentence(a: IntakeAnswers): string {
  return a.account_id.trim()
    ? 'Creates 1 facility, links to an existing customer, no new customer.'
    : 'Creates 1 facility AND 1 new customer.';
}
